import React, { useState } from "react";
import { 
  Star, QrCode, MessageSquare, BarChart3, Zap, Shield, ArrowRight, 
  CheckCircle2, TrendingUp, FileText, Mail, Phone, Sparkles, 
  ChevronDown, ChevronRight, Eye, EyeOff, Brain, Clock, 
  Send, Download, Smartphone, Users, Target, Award, LineChart,
  ArrowUpRight, Check, X, AlertTriangle
} from "lucide-react";

// ===== SCENARIO DATA =====
const scenarioSteps = [
  {
    id: 1,
    title: "Installation du QR Code",
    subtitle: "Jour 1 - Mise en place",
    description: "Le restaurant \"La Belle Étoile\" installe son QR Code personnalisé sur le comptoir et les tables. Chaque client peut scanner le code en quelques secondes depuis son smartphone.",
    visual: "qrcode",
    result: "QR Code prêt en 2 minutes, personnalisé aux couleurs de l'établissement."
  },
  {
    id: 2,
    title: "Collecte d'un avis positif",
    subtitle: "Le client est satisfait",
    description: "Marie vient de dîner et a adoré. Elle scanne le QR Code, donne 5 étoiles et laisse un commentaire. ResponsIA détecte l'avis positif et la redirige automatiquement vers Google pour publier son avis.",
    visual: "positive",
    result: "Avis 5★ publié directement sur Google. Visibilité maximale."
  },
  {
    id: 3,
    title: "Interception d'un avis négatif",
    subtitle: "Le client est mécontent",
    description: "Paul a trouvé le service lent. Il scanne le QR Code et donne 2 étoiles. ResponsIA intercepte l'avis négatif : il reste privé et n'apparaît PAS sur Google. Le restaurateur est alerté immédiatement.",
    visual: "negative",
    result: "Avis négatif intercepté. Votre note Google est protégée."
  },
  {
    id: 4,
    title: "Tableau de bord intelligent",
    subtitle: "Suivi en temps réel",
    description: "Le propriétaire consulte son tableau de bord : nombre d'avis collectés, note moyenne, taux de satisfaction, évolution hebdomadaire. Toutes les métriques clés en un coup d'œil.",
    visual: "dashboard",
    result: "Vision claire et instantanée de votre e-réputation."
  },
  {
    id: 5,
    title: "Plan d'action IA",
    subtitle: "Analyse intelligente",
    description: "L'intelligence artificielle analyse les avis négatifs et génère automatiquement un plan d'action concret avec des recommandations priorisées : points forts à consolider, axes d'amélioration urgents, actions rapides.",
    visual: "actionplan",
    result: "Actions concrètes pour s'améliorer, générées par l'IA."
  },
  {
    id: 6,
    title: "Rapport hebdomadaire PDF",
    subtitle: "Chaque dimanche",
    description: "Chaque dimanche, le restaurateur reçoit par email un rapport PDF professionnel avec graphiques colorés, KPIs visuels, comparaisons semaine/total et le plan d'action IA mis à jour.",
    visual: "report",
    result: "Suivi automatique sans effort. Données claires et actionnables."
  }
];

// ===== VISUAL COMPONENTS =====

const QRCodeVisual = () => (
  <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 max-w-sm mx-auto">
    <div className="text-center">
      <div className="w-20 h-20 bg-gradient-to-br from-violet-600 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <span className="text-white text-2xl font-bold">R</span>
      </div>
      <h4 className="text-lg font-bold text-slate-900 mb-1">Restaurant La Belle Étoile</h4>
      <p className="text-slate-500 text-sm mb-6">Restaurant gastronomique</p>
      <div className="w-48 h-48 bg-slate-100 rounded-xl mx-auto mb-4 flex items-center justify-center border-2 border-dashed border-slate-300">
        <QrCode className="w-32 h-32 text-slate-800" />
      </div>
      <p className="text-xs text-slate-400">Scannez pour donner votre avis</p>
    </div>
  </div>
);

const PositiveReviewVisual = () => (
  <div className="max-w-sm mx-auto space-y-4">
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
          <span className="text-pink-600 font-bold">M</span>
        </div>
        <div>
          <p className="font-semibold text-slate-900">Marie D.</p>
          <p className="text-xs text-slate-400">Il y a 2 minutes</p>
        </div>
      </div>
      <div className="flex gap-1 mb-3">
        {[1,2,3,4,5].map(i => (
          <Star key={i} className="w-6 h-6 text-amber-400 fill-amber-400" />
        ))}
      </div>
      <p className="text-slate-700 text-sm leading-relaxed italic">
        "Excellent restaurant ! Cuisine raffinée, service impeccable et cadre magnifique. Je recommande vivement !"
      </p>
    </div>
    <div className="flex items-center justify-center gap-2 text-emerald-600 font-semibold bg-emerald-50 rounded-xl py-3 px-4">
      <ArrowUpRight className="w-5 h-5" />
      <span className="text-sm">Redirigée automatiquement vers Google</span>
    </div>
  </div>
);

const NegativeReviewVisual = () => (
  <div className="max-w-sm mx-auto space-y-4">
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-red-100">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
          <span className="text-blue-600 font-bold">P</span>
        </div>
        <div>
          <p className="font-semibold text-slate-900">Paul R.</p>
          <p className="text-xs text-slate-400">Il y a 5 minutes</p>
        </div>
      </div>
      <div className="flex gap-1 mb-3">
        {[1,2].map(i => (
          <Star key={i} className="w-6 h-6 text-amber-400 fill-amber-400" />
        ))}
        {[3,4,5].map(i => (
          <Star key={i} className="w-6 h-6 text-slate-200" />
        ))}
      </div>
      <p className="text-slate-700 text-sm leading-relaxed italic">
        "Service trop lent, attente de 45 minutes pour être servi. Personnel peu aimable."
      </p>
    </div>
    <div className="flex items-center justify-center gap-2 text-red-600 font-semibold bg-red-50 rounded-xl py-3 px-4">
      <Shield className="w-5 h-5" />
      <span className="text-sm">Intercepté ! Ne sera PAS publié sur Google</span>
    </div>
  </div>
);

const DashboardVisual = () => (
  <div className="bg-slate-50 rounded-2xl p-6 shadow-lg border border-slate-100">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      {[
        { label: "Avis collectés", value: "47", trend: "+12 cette semaine", color: "text-slate-900" },
        { label: "Note moyenne", value: "4.7/5", trend: null, color: "text-slate-900", stars: true },
        { label: "Avis positifs", value: "89%", trend: "42 avis positifs", color: "text-emerald-600" },
        { label: "À traiter", value: "3", trend: "Avis négatifs", color: "text-amber-600" },
      ].map((kpi, i) => (
        <div key={i} className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-xs text-slate-500 mb-1">{kpi.label}</p>
          <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
          {kpi.stars && (
            <div className="flex gap-0.5 mt-1">
              {[1,2,3,4,5].map(j => (
                <Star key={j} className={`w-3 h-3 ${j <= 4 ? 'text-amber-400 fill-amber-400' : 'text-amber-400'}`} />
              ))}
            </div>
          )}
          {kpi.trend && <p className="text-xs text-slate-400 mt-1">{kpi.trend}</p>}
        </div>
      ))}
    </div>
    <div className="bg-white rounded-xl p-4 border border-slate-200">
      <p className="text-sm font-semibold text-slate-700 mb-3">Évolution des avis</p>
      <div className="flex items-end gap-2 h-24">
        {[3, 5, 4, 7, 6, 8, 12].map((v, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div 
              className="w-full bg-gradient-to-t from-violet-600 to-violet-400 rounded-t-md transition-all"
              style={{ height: `${(v / 12) * 80}px` }}
            />
            <span className="text-[10px] text-slate-400">S{i+1}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ActionPlanVisual = () => (
  <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
        <Brain className="w-5 h-5 text-violet-600" />
      </div>
      <div>
        <h4 className="font-bold text-slate-900">Plan d'action IA</h4>
        <p className="text-xs text-slate-500">Généré automatiquement</p>
      </div>
    </div>
    <div className="space-y-3">
      {[
        { priority: "URGENT", color: "bg-red-100 text-red-700", title: "Améliorer la rapidité du service", desc: "Réduire le temps d'attente à moins de 15 minutes" },
        { priority: "IMPORTANT", color: "bg-amber-100 text-amber-700", title: "Former le personnel à l'accueil", desc: "Programme de formation sur la communication client" },
        { priority: "CONSEIL", color: "bg-emerald-100 text-emerald-700", title: "Consolider la qualité culinaire", desc: "Votre cuisine est votre point fort, continuez d'innover" },
      ].map((item, i) => (
        <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${item.color} whitespace-nowrap mt-0.5`}>
            {item.priority}
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-900">{item.title}</p>
            <p className="text-xs text-slate-500">{item.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ReportVisual = () => (
  <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
          <FileText className="w-5 h-5 text-pink-600" />
        </div>
        <div>
          <h4 className="font-bold text-slate-900">Rapport hebdomadaire</h4>
          <p className="text-xs text-slate-500">Semaine du 24 mars 2026</p>
        </div>
      </div>
      <span className="text-xs bg-violet-100 text-violet-700 px-3 py-1 rounded-full font-medium">PDF</span>
    </div>
    <div className="grid grid-cols-3 gap-3 mb-4">
      {[
        { label: "Nouveaux avis", value: "12", bg: "bg-blue-50", color: "text-blue-700" },
        { label: "Note moyenne", value: "4.5", bg: "bg-emerald-50", color: "text-emerald-700" },
        { label: "Progression", value: "+18%", bg: "bg-violet-50", color: "text-violet-700" },
      ].map((kpi, i) => (
        <div key={i} className={`${kpi.bg} rounded-lg p-3 text-center`}>
          <p className={`text-lg font-bold ${kpi.color}`}>{kpi.value}</p>
          <p className="text-[10px] text-slate-500">{kpi.label}</p>
        </div>
      ))}
    </div>
    <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 rounded-lg p-3">
      <Mail className="w-4 h-4" />
      <span>Envoyé automatiquement chaque dimanche par email</span>
    </div>
  </div>
);

// ===== MAIN COMPONENT =====
const Presentation = () => {
  const [activeStep, setActiveStep] = useState(0);

  const getVisual = (visual) => {
    switch (visual) {
      case "qrcode": return <QRCodeVisual />;
      case "positive": return <PositiveReviewVisual />;
      case "negative": return <NegativeReviewVisual />;
      case "dashboard": return <DashboardVisual />;
      case "actionplan": return <ActionPlanVisual />;
      case "report": return <ReportVisual />;
      default: return null;
    }
  };

  const results4weeks = [
    { label: "Avis Google", before: "12", after: "47", icon: Star },
    { label: "Note moyenne", before: "3.8", after: "4.7", icon: TrendingUp },
    { label: "Avis négatifs sur Google", before: "4/mois", after: "0", icon: Shield },
    { label: "Nouveaux clients/mois", before: "~30", after: "~55", icon: Users },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 py-20 lg:py-28">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-72 h-72 bg-violet-500 rounded-full blur-[120px]" />
          <div className="absolute bottom-20 right-20 w-72 h-72 bg-pink-500 rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8 border border-white/20">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-white/80 text-sm">Présentation confidentielle</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Plus_Jakarta_Sans'] mb-6 leading-tight">
            Comment <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">tripler vos avis Google</span> en 30 jours
          </h1>
          <p className="text-lg lg:text-xl text-slate-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            Découvrez comment ResponsIA transforme la gestion des avis clients 
            avec un cas concret : <strong className="text-white">Restaurant La Belle Étoile</strong>.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            {[
              { icon: QrCode, text: "QR Code intelligent" },
              { icon: Shield, text: "Protection Google" },
              { icon: Brain, text: "IA intégrée" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-slate-300">
                <item.icon className="w-5 h-5 text-violet-400" />
                <span className="text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== THE PROBLEM ===== */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] mb-4">
              Le problème que rencontrent 90% des commerces
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Vos clients satisfaits partent sans laisser d'avis. Vos clients mécontents, eux, écrivent sur Google.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { 
                icon: X, 
                title: "Avis négatifs publics",
                desc: "Un seul avis 1 étoile peut décourager des dizaines de prospects. Il reste visible des mois.",
                color: "bg-red-50 text-red-600 border-red-100"
              },
              { 
                icon: EyeOff,
                title: "Clients satisfaits silencieux", 
                desc: "95% des clients satisfaits ne laissent jamais d'avis spontanément. Un immense potentiel gâché.",
                color: "bg-amber-50 text-amber-600 border-amber-100"
              },
              { 
                icon: AlertTriangle,
                title: "Pas de visibilité",
                desc: "Sans suivi structuré, impossible de savoir ce qui fonctionne et ce qui doit être amélioré.",
                color: "bg-slate-100 text-slate-600 border-slate-200"
              },
            ].map((item, i) => (
              <div key={i} className={`rounded-2xl p-6 border ${item.color}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-white shadow-sm">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== THE SOLUTION - STEP BY STEP ===== */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="inline-block text-sm font-semibold text-violet-600 bg-violet-50 px-4 py-1.5 rounded-full mb-4">
              Cas concret
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] mb-4">
              Comment ça fonctionne, étape par étape
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Suivez le parcours complet du <strong>Restaurant La Belle Étoile</strong>, de l'installation à l'analyse IA.
            </p>
          </div>

          {/* Step navigation */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {scenarioSteps.map((step, i) => (
              <button
                key={step.id}
                onClick={() => setActiveStep(i)}
                className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                  activeStep === i
                    ? "bg-gradient-to-r from-violet-600 to-pink-500 text-white shadow-lg shadow-violet-500/25"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                Étape {step.id}
              </button>
            ))}
          </div>

          {/* Active step content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[450px]">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 bg-gradient-to-br from-violet-600 to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {scenarioSteps[activeStep].id}
                </span>
                <span className="text-sm text-violet-600 font-medium">{scenarioSteps[activeStep].subtitle}</span>
              </div>
              <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">
                {scenarioSteps[activeStep].title}
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6 text-lg">
                {scenarioSteps[activeStep].description}
              </p>
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-emerald-900 text-sm">Résultat</p>
                    <p className="text-emerald-700 text-sm">{scenarioSteps[activeStep].result}</p>
                  </div>
                </div>
              </div>
              {/* Navigation */}
              <div className="flex gap-3 mt-8">
                {activeStep > 0 && (
                  <button
                    onClick={() => setActiveStep(activeStep - 1)}
                    className="px-5 py-2.5 rounded-full text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                  >
                    Précédent
                  </button>
                )}
                {activeStep < scenarioSteps.length - 1 && (
                  <button
                    onClick={() => setActiveStep(activeStep + 1)}
                    className="px-5 py-2.5 rounded-full text-sm font-medium bg-gradient-to-r from-violet-600 to-pink-500 text-white hover:opacity-90 transition-opacity flex items-center gap-2"
                  >
                    Suivant <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex justify-center">
              {getVisual(scenarioSteps[activeStep].visual)}
            </div>
          </div>
        </div>
      </section>

      {/* ===== RESULTS AFTER 4 WEEKS ===== */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 text-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="inline-block text-sm font-semibold text-violet-400 bg-violet-400/10 px-4 py-1.5 rounded-full mb-4 border border-violet-400/20">
              Résultats mesurés
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Plus_Jakarta_Sans'] mb-4">
              Après 4 semaines d'utilisation
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Voici les résultats concrets obtenus par le Restaurant La Belle Étoile.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {results4weeks.map((r, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center">
                <div className="w-12 h-12 bg-violet-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <r.icon className="w-6 h-6 text-violet-400" />
                </div>
                <p className="text-sm text-slate-400 mb-3">{r.label}</p>
                <div className="flex items-center justify-center gap-3 mb-2">
                  <span className="text-xl text-slate-500 line-through">{r.before}</span>
                  <ArrowRight className="w-4 h-4 text-violet-400" />
                  <span className="text-3xl font-bold text-white">{r.after}</span>
                </div>
              </div>
            ))}
          </div>
          
          {/* ROI highlight */}
          <div className="mt-12 bg-gradient-to-r from-violet-600/20 to-pink-600/20 rounded-2xl p-8 border border-violet-500/20 text-center">
            <h3 className="text-2xl font-bold mb-2">Retour sur investissement</h3>
            <p className="text-slate-300 mb-4">
              Pour un abonnement de <strong className="text-white">49€/mois</strong>, le restaurant a gagné environ <strong className="text-white">25 nouveaux clients supplémentaires</strong> en un mois.
            </p>
            <p className="text-3xl font-bold bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
              Soit un ROI de +500%
            </p>
          </div>
        </div>
      </section>

      {/* ===== FEATURES OVERVIEW ===== */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] mb-4">
              Tout est inclus dans ResponsIA
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: QrCode, title: "QR Code personnalisé", desc: "Généré en 2 minutes, aux couleurs de votre établissement. Prêt à imprimer." },
              { icon: Shield, title: "Filtrage intelligent", desc: "Les avis positifs vont sur Google. Les négatifs restent privés pour amélioration." },
              { icon: BarChart3, title: "Tableau de bord", desc: "KPIs en temps réel : avis collectés, note moyenne, tendances, statistiques." },
              { icon: Brain, title: "Plan d'action IA", desc: "L'IA analyse vos avis et génère des recommandations priorisées automatiquement." },
              { icon: FileText, title: "Rapports PDF", desc: "Chaque dimanche, recevez un rapport visuel complet par email avec graphiques." },
              { icon: Smartphone, title: "Sticker NFC", desc: "Générez un autocollant NFC pour que les clients laissent un avis d'un simple tap." },
            ].map((f, i) => (
              <div key={i} className="rounded-2xl p-6 border border-slate-100 hover:shadow-lg hover:border-slate-200 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                  <f.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] mb-4">
              Un tarif simple, tout inclus
            </h2>
            <p className="text-lg text-slate-600">
              Pas de frais cachés. Pas de modules payants.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Monthly */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
              <p className="text-slate-500 text-sm mb-4 font-medium">Mensuel</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-slate-900">49€</span>
                <span className="text-slate-500">/mois TTC</span>
              </div>
              <p className="text-sm text-slate-400 mb-6">Sans engagement, annulable à tout moment</p>
              <ul className="space-y-3 mb-6">
                {["Toutes les fonctionnalités", "3 jours d'essai gratuit", "Support prioritaire"].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            {/* Annual */}
            <div className="bg-white rounded-2xl p-8 border-2 border-violet-500 shadow-lg shadow-violet-500/10 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-gradient-to-r from-violet-600 to-pink-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                  2 mois offerts
                </span>
              </div>
              <p className="text-violet-600 text-sm mb-4 font-medium">Annuel</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-slate-900">490€</span>
                <span className="text-slate-500">/an TTC</span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-slate-400 line-through text-sm">588€</span>
                <span className="text-emerald-600 text-xs font-semibold bg-emerald-50 px-2 py-0.5 rounded-full">-17%</span>
              </div>
              <p className="text-sm text-slate-400 mb-6">Soit 40,83€/mois</p>
              <ul className="space-y-3 mb-6">
                {["Toutes les fonctionnalités", "2 mois gratuits", "Support prioritaire", "Engagement 12 mois"].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-20 bg-gradient-to-br from-violet-600 to-pink-500">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Plus_Jakarta_Sans'] mb-6">
            Prêt à transformer vos avis en atout ?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
            Contactez-nous pour une démonstration personnalisée ou démarrez votre essai gratuit dès maintenant.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="tel:0666027925"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-slate-900 font-semibold rounded-full hover:bg-slate-100 transition-colors shadow-lg"
            >
              <Phone className="w-5 h-5" />
              06 66 02 79 25
            </a>
            <a 
              href="mailto:sabrina.adjarsa@responsia.fr"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full border border-white/30 hover:bg-white/20 transition-colors"
            >
              <Mail className="w-5 h-5" />
              sabrina.adjarsa@responsia.fr
            </a>
          </div>
          <p className="text-white/60 text-sm mt-6">
            ResponsIA - Votre e-réputation, maîtrisée.
          </p>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-8 bg-slate-900 text-center">
        <p className="text-slate-400 text-sm">
          Document confidentiel - ResponsIA &copy; {new Date().getFullYear()} - Tous droits réservés
        </p>
        <p className="text-slate-500 text-xs mt-2">
          www.responsia.fr
        </p>
      </footer>
    </div>
  );
};

export default Presentation;
